import { setPropertyForObject } from "flows/users/utilities"
import { RolesPerService } from "./roles"

export type Address = {
    street_address?: string
    postal_code?: string
    region?: string
    locality?: string
}

export type PatchUser = Partial<User> & {
    service_roles: Record<string, string[]>
}

/**
 * Represents a user. See mapUserFromAPIToModel.
 * Converting this type to a class is tempting, but it caused issues with
 * Immer and copying in the reducer. Specifically, the reducer tests failed
 * to verify object prototype preservation.
 */
export type User = {
    id: string
    created: Date
    email: string
    picture_url: string
    name: string

    given_name?: string
    family_name?: string
    phone_number?: string
    title?: string
    iso_language?: string
    iso_country?: string
    region?: string
    unit?: "metric" | "imperial"
    company_name?: string
    address?: Address

    customer_number?: string
    service_roles: Record<string, string[]>

    /**
     * Indicates whether this user has unsaved changes.
     */
    unsaved_changes?: boolean

    /**
     * Indicates whether this user is currently selected in the UI.
     */
    selected?: boolean

    /**
     * Contains data for sending a patch to the backend.
     */
    patch?: PatchUser
}

export function getPatch(user: User): PatchUser {
    user.patch ??= { service_roles: {} }
    return user.patch
}

/**
 * Adds a service role to the user.
 *
 * @param user the user object
 * @param service the service claim name
 * @param role the role to add
 * @param rolesPerService the list of roles per service, to determine primary roles
 */
export function addUserRole(user: User, service: string, role: string, rolesPerService: RolesPerService) {
    const roles = user.service_roles[service] || []
    if (!roles.includes(role)) {
        let newList = [...roles, role]

        // Only apply primary/secondary role rules if rolesPerService is provided,
        // to be able to preserve existing tests.
        if (rolesPerService.length > 0) {
            const allRoles = rolesPerService.find(s => s.claim_name === service)?.roles ?? []
            const isPrimary = allRoles.find(r => r.id === role)?.attributes.is_primary ?? false

            if (isPrimary) {
                // Remove any other primary roles
                const primaryRoles = allRoles.filter(r => r.attributes.is_primary).map(r => r.id)
                newList = newList.filter(r => !primaryRoles.includes(r) || r === role)
            } else {
                // Check if there is a primary role
                const primaryRoleIds = allRoles.filter(r => r.attributes.is_primary).map(r => r.id)
                const hasPrimaryRole = roles.some(r => primaryRoleIds.includes(r))

                if (!hasPrimaryRole) {
                    // Cannot add secondary role when no primary role exists
                    return
                }
            }
        }

        user.service_roles[service] = newList

        const patch = getPatch(user)
        patch.service_roles[service] = newList

        user.unsaved_changes = true
    }
}

/**
 * Removes a service role from the user.
 *
 * @param user the user object
 * @param service the service claim name
 * @param role the role to remove
 * @param rolesPerService the list of roles per service, to determine primary roles
 */
export function removeUserRole(user: User, service: string, role: string, rolesPerService: RolesPerService) {
    const roles = user.service_roles[service] || []
    if (!roles.includes(role)) {
        return
    }

    const allRoles = rolesPerService.find(s => s.claim_name === service)?.roles ?? []
    const isPrimary = allRoles.find(r => r.id === role)?.attributes.is_primary ?? false

    if (isPrimary) {
        // Check if there are any secondary roles
        const secondaryRoleIds = allRoles.filter(r => !r.attributes.is_primary).map(r => r.id)
        const hasSecondaryRoles = roles.some(r => secondaryRoleIds.includes(r))

        if (hasSecondaryRoles) {
            // Cannot remove primary role when secondary roles exist
            return
        }
    }

    const newList = roles.filter(r => r !== role)
    user.service_roles[service] = newList

    const patch = getPatch(user)
    patch.service_roles[service] = newList

    user.unsaved_changes = true
}

/**
 * Sets a value for a dynamic property on the user.
 *
 * @param user the user object
 * @param propertyPath the property path
 * @param value the value to set
 */
export function setUserProperty(user: User, propertyPath: string[], value: any) {
    setPropertyForObject(
        user,
        propertyPath,
        value
    )
    user.unsaved_changes = true

    // Record in patch object for subsequent save
    setPropertyForObject(
        getPatch(user),
        propertyPath,
        value
    )
}
